import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Popconfirm,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { errorMessage, RequestError } from '../../MarketExplorer/api';
import { deleteMemory, listMemories } from './api';
import { Memory } from './types';

type Props = {
  /** Bumped when a turn ends: the builder may have remembered or forgotten something. */
  refreshKey?: number;
  /** The address of a chat, so "from chat #N" opens it. */
  threadHref: (threadId: number) => string;
};

const EMPTY =
  'Nothing remembered yet. Tell the builder "remember …" in plain words.';

const SCOPE_COLOURS: Record<string, string | undefined> = {
  user: 'blue',
  global: 'purple',
};

const day = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString([], {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
};

/**
 * What the builder was told to remember across chats (its `remember` and
 * `forget` tools write them), under the chat list. Collapsed, with the count
 * in its header; each memory shows its scope, when it was learnt and the chat
 * it came from, and can be deleted after a confirm. Re-read whenever
 * `refreshKey` changes.
 */
const Memories: React.FC<Props> = ({ refreshKey = 0, threadHref }) => {
  const [memories, setMemories] = useState<Memory[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    listMemories(controller.signal)
      .then(value => {
        if (controller.signal.aborted) return;
        setMemories(value?.results || []);
        setError('');
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setError(errorMessage(reason) || 'The memories could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [refreshKey, retryKey]);

  const drop = (id: number) =>
    setMemories(list => (list || []).filter(item => item.id !== id));

  const remove = async (memory: Memory) => {
    setDeleting(memory.id);
    setDeleteError('');
    try {
      await deleteMemory(memory.id);
      drop(memory.id);
    } catch (reason) {
      // Already gone (the builder forgot it, or another tab did): so be it.
      if (reason instanceof RequestError && reason.status === 404)
        drop(memory.id);
      else
        setDeleteError(
          errorMessage(reason) || 'The memory could not be deleted.',
        );
    } finally {
      setDeleting(null);
    }
  };

  const body =
    loading && memories == null ? (
      <div className="blueprint-builder__center">
        <Spin size="small" />
      </div>
    ) : error && memories == null ? (
      <Alert
        type="error"
        showIcon
        title={error}
        action={
          <Button size="small" onClick={() => setRetryKey(key => key + 1)}>
            Retry
          </Button>
        }
      />
    ) : !memories?.length ? (
      <Typography.Text type="secondary" className="blueprint-builder__muted">
        {EMPTY}
      </Typography.Text>
    ) : (
      <ul className="blueprint-builder__memory-list">
        {memories.map(memory => (
          <li
            key={memory.id}
            className="blueprint-builder__memory"
            data-testid={`memory-${memory.id}`}
          >
            <div className="blueprint-builder__memory-text">{memory.text}</div>
            <div className="blueprint-builder__memory-meta">
              <Tag color={SCOPE_COLOURS[memory.scope]}>{memory.scope}</Tag>
              {day(memory.created_at) && <span>{day(memory.created_at)}</span>}
              {memory.source_thread_id != null && (
                <Link to={threadHref(memory.source_thread_id)}>
                  from chat #{memory.source_thread_id}
                </Link>
              )}
              <Popconfirm
                title="Delete this memory?"
                description="The builder will no longer recall it."
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Keep"
                onConfirm={() => remove(memory)}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Delete memory"
                  loading={deleting === memory.id}
                  className="blueprint-builder__memory-delete"
                />
              </Popconfirm>
            </div>
          </li>
        ))}
      </ul>
    );

  return (
    <Collapse
      ghost
      size="small"
      className="blueprint-builder__memories"
      items={[
        {
          key: 'memories',
          label: `Memories${memories ? ` (${memories.length})` : ''}`,
          children: (
            <div className="blueprint-builder__memories-body">
              {deleteError && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  onClose={() => setDeleteError('')}
                  title={deleteError}
                />
              )}
              {body}
            </div>
          ),
        },
      ]}
    />
  );
};

export default Memories;
